import { Component, ElementRef, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, mergeWith, switchMap } from 'rxjs/operators';
import { SidebarItemDefinition } from '../../../common/model/sidebar-item';
import { ChatService } from '../../../common/service/chat-service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { UpdatesModel, UpdatesService } from '../../service/updates.service';

@Component({
    selector: 'app-dashboard-header',
    templateUrl: './dashboard-header.component.html',
    styleUrls: ['./dashboard-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardHeaderComponent implements OnDestroy, OnInit {
  @ViewChild('mobileView') mobileView: ElementRef;
  public menu: SidebarItemDefinition[];
  public showMenu: boolean;
  private _previousScroll: number;
  private _menuSubscription: Subscription;
  private _updatesSubscription: Subscription;
  private _unreadSubscription: Subscription;
  @ViewChild('navigationSidebarContent', { static: true }) navigationSidebarContent: TemplateRef<any>;
  public isAdmin$ = this._accountService.isAdmin$;

  constructor(
    private readonly _nativeWindow: NativeWindowService,
    private readonly _renderer: Renderer2,
    private readonly _router: Router,
    private readonly _updates: UpdatesService,
    private readonly _accountService: UserAccountService,
    private readonly _chatService: ChatService,
    private readonly _sidebarService: SidebarService
  ) {
    this._menuSubscription = _accountService.current.subscribe({ next: account => (this.menu = account.sidebar) });

    this._updatesSubscription = _accountService.current
      .pipe(
        mergeWith(this._router.events.pipe(filter(e => this.menu && e instanceof NavigationEnd))),
        switchMap(() => this._updates.getUpdates())
      )
      .subscribe({ next: updates => this.setUpdates(updates) });

    this._unreadSubscription = this._updatesSubscription = _accountService.current
      .pipe(
        mergeWith(this._router.events.pipe(filter(e => this.menu && e instanceof NavigationEnd))),
        switchMap(() => this._chatService.hasUnreadMessage())
      )
      .subscribe({ next: messages => this.setUnreadMessages(messages.hasUnreadMessages) });
  }

  setUpdates(updates: UpdatesModel) {
    if (!this.menu) return;
    const faultNotification = this.menu.find(item => item.header === 'DASHBOARD.FAULT_NOTIFICATION_ITEM_HEADER');
    if (faultNotification) {
      faultNotification.updates = updates.unreadFaultNotificationsCount;
    }

    // TODO: unblock after clarification and update of errand notifications calc query
    // const errands = this.menu.find(item => item.header === 'DASHBOARD.MENU.ERRANDS');
    // if (errands) {
    //   errands.updates = updates.newRoundingErrandsCount + updates.newAuthorityInspectionErrandsCount;
    // }
  }

  setUnreadMessages(hasUnread: boolean) {
    if (!this.menu) return;
    const faultNotification = this.menu.find(item => item.header === 'DASHBOARD.FAULT_NOTIFICATION_ITEM_HEADER');
    if (faultNotification) {
      faultNotification.hasUnreadMessages = hasUnread || false;
    }
  }

  ngOnDestroy() {
    this._menuSubscription?.unsubscribe();
    this._updatesSubscription?.unsubscribe();
    this._unreadSubscription?.unsubscribe();
  }

  ngOnInit() {
    if (this._nativeWindow.window) {
      this._previousScroll = this._nativeWindow.window.pageYOffset;
    }
  }

  onMenuBtnClick = () => {
    this._showNavigation();
  };

  onMenuItemClick() {
    this.showMenu = false;
  }

  onMenuMouseLeave() {
    this.showMenu = false;
  }

  onScroll() {
    if (!this._nativeWindow.window || !this.mobileView) {
      return;
    }

    const current = this._nativeWindow.window.pageYOffset;
    if (this._previousScroll > current) {
      this._renderer.setStyle(this.mobileView.nativeElement, 'top', 0);
    } else {
      this._renderer.setStyle(this.mobileView.nativeElement, 'top', '-70px');
    }
    this._previousScroll = current;
  }

  private _showNavigation() {
    this._sidebarService.setSidebarContent({
      template: this.navigationSidebarContent,
      showDefaultHeader: false,
    });
    this._sidebarService.toggleSidebar(true);
  }

  closeSidebar = () => {
    this._sidebarService.toggleSidebar(false);
  };
}
